import type { CogniteFunctionDefinition } from '#core/hooks/fusion/useCogniteFunction';
import { z } from 'zod';

export const ResChemRequest = z.object({
  model_key: z
    .enum(['reservoir_chemistry'])
    .default('reservoir_chemistry')
    .describe('The key of the model to be used for prediction'),
  ml_inputs: z.object({
    sg: z
      .number()
      .min(1, { error: 'SG must be in range 1.0~1.2' })
      .max(2, { error: 'SG must be in range 1.0~1.2' })
      .describe('Specific Gravity (SG) value'),
    reservoir: z
      .string({ error: 'Reservoir must be specified' })
      .describe('Reservoir name, must be equal to the `name` of a reservoir in the `Reservoir` view.'),
  }),
});

export type ResChemRequest = z.input<typeof ResChemRequest>;

export const ResChemResponse = z.object({
  model: z.enum(['reservoir_chemistry']).describe('The key of the model used for prediction'),
  predictions: z.object({
    predicted_concentrations: z.object({
      cl: z.number().describe('Predicted concentration of Chloride (Cl)'),
      na: z.number().describe('Predicted concentration of Sodium (Na)'),
      salinity: z.number().describe('Predicted Salinity'),
      so4: z.number().describe('Predicted concentration of Sulfate (SO4)'),
      sr: z.number().describe('Predicted concentration of Strontium (Sr)'),
    }),
    predicted_concentrations_uncertainty: z.object({
      cl: z.number().describe('Uncertainty of predicted concentration of Chloride (Cl)'),
      na: z.number().describe('Uncertainty of predicted concentration of Sodium (Na)'),
      salinity: z.number().describe('Uncertainty of predicted Salinity'),
      so4: z.number().describe('Uncertainty of predicted concentration of Sulfate (SO4)'),
      sr: z.number().describe('Uncertainty of predicted concentration of Strontium (Sr)'),
    }),
    units: z.enum(['mg/L']).describe('The units of the predicted concentrations'),
  }),
});

export type ResChemResponse = z.input<typeof ResChemResponse>;

export const ResChemFunction = {
  externalId: 'fn_scale_poc_ml_predict_uc2',
  request: ResChemRequest,
  response: ResChemResponse,
} as const satisfies CogniteFunctionDefinition;

export type ResChemFunction = typeof ResChemFunction;
